import { useFlags } from 'launchdarkly-react-client-sdk'
import { observer } from 'mobx-react-lite'
import { ChangeEventHandler, ComponentProps, FC } from 'react'
import ArpRegistry from '../../../features/arp-registry/ArpRegistry'
import { useAuth } from '../../hooks/useAuth'
import { css, styled } from '../../lib/stitches'
import { IStore, useTreeState } from '../../stores/Root'
import { IStudioStore } from '../../stores/StudioStore'
import { IconButton, Spacer } from '../core/Components'
import { CloudChecked, CloudError, ResponsiveIcons, TrashIcon } from '../core/icons'
import withTooltip from '../core/withTooltip'
import { gray4 } from '../platform/color'
import ProfileCard from '../platform/dashboard/ProfileCard'
import PlaybackControls, { StudioHeaderPlaybackControls } from '../player/common/PlaybackControls'
import { formatTime } from '../player/PlayerUtils'
import { HEADER_HEIGHT } from '../studio/Studio'
import MetronomeButton from './controls/MetronomeButton'
import Tempo from './controls/Tempo'
import UndoRedo from './controls/UndoRedo'
import VolumeSlider from './controls/VolumeSlider'
import MintModal, { StyledButton } from './mint/mint-modal'
import { MintSoundModal } from './mint/upload-sound-modal/MintSoundModal'
import { segmentStudioButton } from './StudioUtils'
export const KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']

const MARGIN = { marginLeft: '16px', marginRight: '16px' }

const StyledStudioClockContainer = styled('div', {
  fontFamily: 'Azeret Mono',
  fontSize: '16px',
  lineHeight: '22px',
  fontWeight: 300,
  letterSpacing: '-1px',
  color: 'rgba(0,0,0,.5)',
  userSelect: 'none',
  cursor: 'default',
  ...MARGIN
})

const StyledHeaderControlsContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '1',
  marginLeft: '16px',
  marginRight: '16px'
})

const StyledTitleInput = styled('input', {
  border: 'none',
  background: 'none',
  textDecoration: 'none',
  outline: 'none',
  padding: '6px 10px',
  borderRadius: '6px',
  transition: 'all 100ms ease',
  textOverflow: 'ellipsis',
  width: '100%',
  minWidth: '150px',
  maxWidth: '400px',
  fontSize: '16px',
  lineHeight: '14px',
  fontWeight: 500,
  '&:focus': {
    boxShadow: `0px 0px 0px 1px rgba(0,0,0,.8) !important`
  },
  '&:hover': {
    boxShadow: `0px 0px 0px 1px rgba(0,0,0,.15)`
  }
})

const StyledHeaderContainer = styled('div', {
  background: '$gray01',
  borderBottom: `1px solid ${gray4}`,
  display: 'flex',
  position: 'relative',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0px 16px'
})

const Container = styled('div', {
  display: 'flex',
  alignItems: 'center',
  variants: {
    col: {
      true: {
        flexDirection: 'column',
        alignItems: 'stretch'
      }
    }
  }
})

const iconStyles = css({ width: '17px', height: '17px' })

const StyledHeaderIconButton = styled(IconButton, MARGIN)
const Saved = styled(CloudChecked, { width: '20px', height: '20px' })
const Unsaved = styled(CloudError, { width: '20px', height: '20px' })

const HeaderControlsContainer = StyledHeaderControlsContainer
const StudioClockContainer = StyledStudioClockContainer
const HeaderButton = withTooltip(StyledHeaderIconButton)
const TitleInput = StyledTitleInput
const HeaderContainer = StyledHeaderContainer

const IconButtonWithTooltip = withTooltip(IconButton)

const ClearTrackButton: FC<ComponentProps<typeof HeaderButton>> = (props) => (
  <HeaderButton {...props} tooltipText={'Clear Song'}>
    <TrashIcon className={iconStyles()} />
  </HeaderButton>
)

const StudioPlaybackControls: FC = observer(() => {
  const { studio } = useTreeState()
  const {
    isPlaying,
    repeatSection,
    play,
    pause,
    stop,
    toggleRepeatSection,
    toggleRecord,
    isRecording
  } = studio
  const handlePlayPause = () => {
    isPlaying ? pause() : play()
  }
  const flags = useFlags()

  if (!flags.midiRecording) {
    return (
      <PlaybackControls
        onPlayPause={handlePlayPause}
        onRepeat={() => {
          toggleRepeatSection()
          segmentStudioButton('Toggle Repeat', 'studio header')
        }}
        onStop={() => {
          stop()
        }}
        isPlaying={isPlaying}
        isRepeat={repeatSection}
        disabled={!ArpRegistry.readyForStudioUse}
        css={MARGIN}
      />
    )
  }
  return (
    <StudioHeaderPlaybackControls
      onPlayPause={handlePlayPause}
      onStop={() => {
        stop()
      }}
      onRecord={() => toggleRecord()}
      isRecording={isRecording}
      isPlaying={isPlaying}
      isRepeat={repeatSection}
      disabled={!ArpRegistry.readyForStudioUse}
      css={MARGIN}
    />
  )
})

type ClockProps = ComponentProps<typeof StudioClockContainer> & { time: number }
const Clock: FC<ClockProps> = observer(({ time, ...props }) => {
  const formattedTime = formatTime(time, true)
  return <StudioClockContainer {...props}>{formattedTime}</StudioClockContainer>
})

const StudioClock: FC = observer(() => {
  const { studio } = useTreeState()
  const { currentTimestampInSeconds } = studio
  return <Clock time={currentTimestampInSeconds} />
})

/**
 * Business logic for header controls
 */
const useHeaderControls = () => {
  const { studio } = useTreeState() as IStore
  const { clearSong, setMasterGain, currentGain, song } = studio
  return {
    // This clears the studio to have a default piano and drum stem. If we want a totally clear studio, replace with "clearSongAndName()"
    handleClear: clearSong,
    gain: currentGain,
    handleGainChange: setMasterGain,
    song
  }
}

/**
 * Header controls component
 */
const HeaderControls: FC = observer(() => {
  const { handleClear, song, gain, handleGainChange } = useHeaderControls()
  return (
    <HeaderControlsContainer>
      <UndoRedo />
      <ClearTrackButton
        onClick={() => {
          handleClear()
          segmentStudioButton('Clear Track', 'studio header')
        }}
      />
      <StudioClock />
      <StudioPlaybackControls />
      <Tempo song={song} />
      <MetronomeButton />
      <VolumeSlider value={gain} setGain={handleGainChange} />
    </HeaderControlsContainer>
  )
})

export const useSongTitle = () => {
  const { studio } = useTreeState()
  const { setSongName, name } = studio.song

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.target.value.length <= 40) {
      setSongName(event.target.value)
    }
  }

  return { name, handleChange }
}

const SongTitle: FC = observer(() => {
  const { name, handleChange } = useSongTitle()
  return <TitleInput onChange={handleChange} value={name} placeholder={'Untitled'} />
})

type SavedIndicatorProps = { studio: IStudioStore }
const SavedIndicator: FC<SavedIndicatorProps> = observer(({ studio }) => {
  const { songChangedSinceSave: changed } = studio
  const icon = changed ? <Unsaved /> : <Saved />
  const label = changed ? 'Changes have not beed saved' : 'Draft saved!'
  return <IconButtonWithTooltip tooltipText={label}>{icon}</IconButtonWithTooltip>
})

const UploadSoundsButton: FC = () => {
  const flags = useFlags()
  if (!flags.uploadSoundsModal) return null
  return (
    <MintSoundModal
      trigger={
        <StyledButton
          css={{
            background: 'linear-gradient(93.07deg, #8BACFF -3.71%, #C2ADFC 107.34%)',
            marginRight: '8px',
            color: '$black'
          }}
          onClick={() => {
            segmentStudioButton('Upload Sounds', 'studio header')
          }}
        >
          <ResponsiveIcons.Upload />
        </StyledButton>
      }
    />
  )
}

type StudioHeaderProps = {}
const StudioHeader: FC<StudioHeaderProps> = observer(({}) => {
  const { user } = useAuth()
  return (
    <HeaderContainer style={{ height: HEADER_HEIGHT }}>
      <Container css={{ width: '-webkit-fill-available' }}>
        <SongTitle />
      </Container>

      <Spacer />
      <HeaderControls />
      <Spacer />
      <Container css={{ justifyContent: 'end', width: '-webkit-fill-available' }}>
        <MintModal />
        <UploadSoundsButton />
        <ProfileCard hideCardText />
      </Container>
    </HeaderContainer>
  )
})

export default StudioHeader
